/**
 * Retro Arcade Sound Effects System
 * Generates 8-bit style sounds using Web Audio API
 */

type SoundType =
  | 'coin'
  | 'powerup'
  | 'select'
  | 'error'
  | 'win'
  | 'lose'
  | 'click'
  | 'whoosh'
  | 'combo'
  | 'levelup'
  | 'hurt'
  | 'explosion'
  | 'laser'
  | 'achievement'
  | 'matchReady';

// Sound categories for granular control
export type SoundCategory = 'ui' | 'gameplay' | 'notification';

export interface SoundCategorySettings {
  ui: boolean; // clicks, select, error
  gameplay: boolean; // combo, powerup, win, lose, hurt, explosion, laser, coin, whoosh
  notification: boolean; // matchReady, achievement, levelup
}

// Map each sound type to its category
const SOUND_CATEGORIES: Record<SoundType, SoundCategory> = {
  click: 'ui',
  select: 'ui',
  error: 'ui',
  combo: 'gameplay',
  powerup: 'gameplay',
  win: 'gameplay',
  lose: 'gameplay',
  hurt: 'gameplay',
  explosion: 'gameplay',
  laser: 'gameplay',
  coin: 'gameplay',
  whoosh: 'gameplay',
  matchReady: 'notification',
  achievement: 'notification',
  levelup: 'notification',
};

class ArcadeSoundManager {
  private context: AudioContext | null = null;
  private enabled = true;
  private volume = 0.3;
  private sounds = new Map<string, AudioBuffer>();
  private categorySettings: SoundCategorySettings = {
    ui: true,
    gameplay: true,
    notification: true,
  };

  constructor() {
    if (typeof window !== 'undefined') {
      this.context = new (window.AudioContext || (window as any).webkitAudioContext)();
      // Resume context on first user interaction
      document.addEventListener('click', () => this.resume(), { once: true });
    }
  }

  private async resume() {
    if (this.context?.state === 'suspended') {
      await this.context.resume();
    }
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  setCategoryEnabled(category: SoundCategory, enabled: boolean) {
    this.categorySettings[category] = enabled;
  }

  getCategoryEnabled(category: SoundCategory): boolean {
    return this.categorySettings[category];
  }

  setCategorySettings(settings: SoundCategorySettings) {
    this.categorySettings = { ...settings };
  }

  getCategorySettings(): SoundCategorySettings {
    return { ...this.categorySettings };
  }

  private isSoundEnabled(sound: SoundType): boolean {
    if (!this.enabled) return false;
    const category = SOUND_CATEGORIES[sound];
    return this.categorySettings[category];
  }

  private createOscillator(type: OscillatorType = 'square'): OscillatorNode {
    if (!this.context) throw new Error('AudioContext not initialized');
    return this.context.createOscillator();
  }

  private createGain(): GainNode {
    if (!this.context) throw new Error('AudioContext not initialized');
    return this.context.createGain();
  }

  /**
   * Coin pickup sound
   */
  private playCoin() {
    if (!this.context) return;
    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'square';
    osc.frequency.setValueAtTime(988, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1319, this.context.currentTime + 0.1);

    gain.gain.setValueAtTime(this.volume * 0.3, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.15);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.15);
  }

  /**
   * Power-up sound
   */
  private playPowerup() {
    if (!this.context) return;
    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'square';
    osc.frequency.setValueAtTime(200, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(600, this.context.currentTime + 0.2);

    gain.gain.setValueAtTime(this.volume * 0.3, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.25);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.25);
  }

  /**
   * Menu select sound
   */
  private playSelect() {
    if (!this.context) return;
    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'square';
    osc.frequency.setValueAtTime(800, this.context.currentTime);

    gain.gain.setValueAtTime(this.volume * 0.2, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.08);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.08);
  }

  /**
   * Error sound
   */
  private playError() {
    if (!this.context) return;
    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, this.context.currentTime + 0.3);

    gain.gain.setValueAtTime(this.volume * 0.3, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.3);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.3);
  }

  /**
   * Victory sound
   */
  private playWin() {
    if (!this.context) return;
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6

    notes.forEach((freq, i) => {
      const osc = this.createOscillator();
      const gain = this.createGain();

      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, this.context!.currentTime + i * 0.12);

      gain.gain.setValueAtTime(this.volume * 0.3, this.context!.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.01, this.context!.currentTime + i * 0.12 + 0.2);

      osc.connect(gain);
      gain.connect(this.context!.destination);

      osc.start(this.context!.currentTime + i * 0.12);
      osc.stop(this.context!.currentTime + i * 0.12 + 0.2);
    });
  }

  /**
   * Defeat sound
   */
  private playLose() {
    if (!this.context) return;
    const notes = [494, 440, 392, 330]; // B4, A4, G4, E4

    notes.forEach((freq, i) => {
      const osc = this.createOscillator();
      const gain = this.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, this.context!.currentTime + i * 0.15);

      gain.gain.setValueAtTime(this.volume * 0.3, this.context!.currentTime + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.01, this.context!.currentTime + i * 0.15 + 0.25);

      osc.connect(gain);
      gain.connect(this.context!.destination);

      osc.start(this.context!.currentTime + i * 0.15);
      osc.stop(this.context!.currentTime + i * 0.15 + 0.25);
    });
  }

  /**
   * Click sound
   */
  private playClick() {
    if (!this.context) return;
    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'square';
    osc.frequency.setValueAtTime(600, this.context.currentTime);

    gain.gain.setValueAtTime(this.volume * 0.15, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.05);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.05);
  }

  /**
   * Whoosh sound
   */
  private playWhoosh() {
    if (!this.context) return;
    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(1200, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(300, this.context.currentTime + 0.2);

    gain.gain.setValueAtTime(this.volume * 0.2, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.2);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.2);
  }

  /**
   * Combo sound
   */
  private playCombo() {
    if (!this.context) return;
    const osc1 = this.createOscillator();
    const osc2 = this.createOscillator();
    const gain = this.createGain();

    osc1.type = 'square';
    osc1.frequency.setValueAtTime(600, this.context.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(1200, this.context.currentTime + 0.1);

    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(800, this.context.currentTime);
    osc2.frequency.exponentialRampToValueAtTime(1600, this.context.currentTime + 0.1);

    gain.gain.setValueAtTime(this.volume * 0.25, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.15);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(this.context.destination);

    osc1.start(this.context.currentTime);
    osc2.start(this.context.currentTime);
    osc1.stop(this.context.currentTime + 0.15);
    osc2.stop(this.context.currentTime + 0.15);
  }

  /**
   * Level up sound
   */
  private playLevelup() {
    if (!this.context) return;
    const notes = [392, 523, 659, 784, 1047]; // G4, C5, E5, G5, C6

    notes.forEach((freq, i) => {
      const osc = this.createOscillator();
      const gain = this.createGain();

      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, this.context!.currentTime + i * 0.08);

      gain.gain.setValueAtTime(this.volume * 0.25, this.context!.currentTime + i * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.01, this.context!.currentTime + i * 0.08 + 0.15);

      osc.connect(gain);
      gain.connect(this.context!.destination);

      osc.start(this.context!.currentTime + i * 0.08);
      osc.stop(this.context!.currentTime + i * 0.08 + 0.15);
    });
  }

  /**
   * Hurt sound
   */
  private playHurt() {
    if (!this.context) return;
    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(300, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, this.context.currentTime + 0.15);

    gain.gain.setValueAtTime(this.volume * 0.3, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.15);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.15);
  }

  /**
   * Explosion sound
   */
  private playExplosion() {
    if (!this.context) return;
    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(400, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, this.context.currentTime + 0.4);

    gain.gain.setValueAtTime(this.volume * 0.4, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.4);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.4);
  }

  /**
   * Laser sound
   */
  private playLaser() {
    if (!this.context) return;
    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(1000, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(200, this.context.currentTime + 0.1);

    gain.gain.setValueAtTime(this.volume * 0.25, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.1);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.1);
  }

  /**
   * Achievement unlocked sound
   */
  private playAchievement() {
    if (!this.context) return;
    const notes = [523, 659, 784, 1047, 1319]; // C5, E5, G5, C6, E6

    notes.forEach((freq, i) => {
      const osc = this.createOscillator();
      const gain = this.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, this.context!.currentTime + i * 0.1);

      gain.gain.setValueAtTime(this.volume * 0.2, this.context!.currentTime + i * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.01, this.context!.currentTime + i * 0.1 + 0.3);

      osc.connect(gain);
      gain.connect(this.context!.destination);

      osc.start(this.context!.currentTime + i * 0.1);
      osc.stop(this.context!.currentTime + i * 0.1 + 0.3);
    });
  }

  /**
   * Speak an announcement using Web Speech API with arcade-style voice
   */
  private speakAnnouncement(text: string, pitch = 1.2, rate = 1.1) {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.pitch = pitch; // Higher pitch for arcade feel
    utterance.rate = rate; // Slightly faster for energy
    utterance.volume = Math.min(1, this.volume * 2); // Boost voice volume

    // Try to find a good voice (prefer English, male voices often sound more arcade-like)
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(
      (v) =>
        v.lang.startsWith('en') &&
        (v.name.includes('Male') || v.name.includes('Daniel') || v.name.includes('Alex')),
    );
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    window.speechSynthesis.speak(utterance);
  }

  /**
   * Match ready notification sound
   * A distinctive rising two-note alert (C5 -> G5) followed by "READY, GO!" voice
   */
  private playMatchReady() {
    if (!this.context) return;

    // Two-tone alert: C5 -> G5 (perfect fifth interval)
    const tones = [523, 784];

    tones.forEach((freq, i) => {
      const osc = this.createOscillator();
      const gain = this.createGain();

      osc.type = 'sine';
      const startTime = this.context!.currentTime + i * 0.15;
      osc.frequency.setValueAtTime(freq, startTime);

      // Higher volume to be attention-grabbing
      gain.gain.setValueAtTime(this.volume * 0.5, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);

      osc.connect(gain);
      gain.connect(this.context!.destination);

      osc.start(startTime);
      osc.stop(startTime + 0.2);
    });

    // Speak "READY, GO!" after the tones complete
    setTimeout(() => {
      this.speakAnnouncement('READY, GO!', 1.3, 1.0);
    }, 400);
  }

  /**
   * Play a sound effect
   * @param sound - The sound type to play
   * @param volumeMultiplier - Optional volume multiplier (0-1)
   */
  play(sound: SoundType, volumeMultiplier?: number) {
    if (!this.isSoundEnabled(sound) || !this.context) return;

    // Temporarily adjust volume if multiplier provided
    const originalVolume = this.volume;
    if (volumeMultiplier !== undefined) {
      this.volume = originalVolume * Math.max(0, Math.min(1, volumeMultiplier));
    }

    try {
      this.resume();

      switch (sound) {
        case 'coin':
          this.playCoin();
          break;
        case 'powerup':
          this.playPowerup();
          break;
        case 'select':
          this.playSelect();
          break;
        case 'error':
          this.playError();
          break;
        case 'win':
          this.playWin();
          break;
        case 'lose':
          this.playLose();
          break;
        case 'click':
          this.playClick();
          break;
        case 'whoosh':
          this.playWhoosh();
          break;
        case 'combo':
          this.playCombo();
          break;
        case 'levelup':
          this.playLevelup();
          break;
        case 'hurt':
          this.playHurt();
          break;
        case 'explosion':
          this.playExplosion();
          break;
        case 'laser':
          this.playLaser();
          break;
        case 'achievement':
          this.playAchievement();
          break;
        case 'matchReady':
          this.playMatchReady();
          break;
      }
    } catch (error) {
      console.error('Error playing sound:', error);
    } finally {
      // Restore original volume
      this.volume = originalVolume;
    }
  }

  /**
   * Play a chip tick sound for counting animations
   * Varies pitch based on value for satisfying cascade
   */
  playChipTick(value: number) {
    if (!this.enabled || !this.categorySettings.gameplay || !this.context) return;

    const osc = this.createOscillator();
    const gain = this.createGain();

    // Pitch increases with value (clamped)
    const basePitch = 400 + Math.min(value / 10, 400);
    osc.type = 'square';
    osc.frequency.setValueAtTime(basePitch, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(basePitch * 1.2, this.context.currentTime + 0.03);

    gain.gain.setValueAtTime(this.volume * 0.15, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.05);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.05);
  }

  /**
   * Play multiplier hit sound
   */
  playMultHit(multiplier: number) {
    if (!this.enabled || !this.categorySettings.gameplay || !this.context) return;

    const osc = this.createOscillator();
    const gain = this.createGain();

    // Higher multiplier = higher pitch
    const basePitch = 600 + Math.min(multiplier * 100, 600);
    osc.type = 'square';
    osc.frequency.setValueAtTime(basePitch, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(basePitch * 1.5, this.context.currentTime + 0.08);

    gain.gain.setValueAtTime(this.volume * 0.25, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.12);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.12);
  }

  /**
   * Play card slide sound
   */
  playCardSlide() {
    if (!this.enabled || !this.categorySettings.gameplay || !this.context) return;

    const osc = this.createOscillator();
    const gain = this.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, this.context.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, this.context.currentTime + 0.1);

    gain.gain.setValueAtTime(this.volume * 0.1, this.context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.1);

    osc.connect(gain);
    gain.connect(this.context.destination);

    osc.start(this.context.currentTime);
    osc.stop(this.context.currentTime + 0.1);
  }
}

// Export singleton instance
export const soundManager = new ArcadeSoundManager();

// Helper hook for React components
export const useSounds = () => {
  return {
    play: (sound: SoundType) => soundManager.play(sound),
    setEnabled: (enabled: boolean) => soundManager.setEnabled(enabled),
    setVolume: (volume: number) => soundManager.setVolume(volume),
    setCategoryEnabled: (category: SoundCategory, enabled: boolean) =>
      soundManager.setCategoryEnabled(category, enabled),
    getCategoryEnabled: (category: SoundCategory) => soundManager.getCategoryEnabled(category),
    setCategorySettings: (settings: SoundCategorySettings) =>
      soundManager.setCategorySettings(settings),
    getCategorySettings: () => soundManager.getCategorySettings(),
  };
};
